import React, { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { t } from '../common/LocalizationProvider';
import userAttributes from '../attributes/userAttributes';
import EditItemView from '../shared/EditItemView';
import EditAttributesView from '../attributes/EditAttributesView';
import LinkField from '../form/LinkField';
import DeleteAccountDialog from "../shared/DeleteAccountDialog";
import { organizationActions } from "../store";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  makeStyles,
  Typography,
  Grid,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  TextField,
  Snackbar,
  Button,
  Box
} from '@material-ui/core';
import Alert from "@material-ui/lab/Alert";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { DeleteForeverOutlined } from "@material-ui/icons";

import { cyberStyles } from '../cssShared/cyberTheme';
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import VisibilityIcon from "@material-ui/icons/Visibility";

const useStyles = makeStyles((theme) => ({
  ...cyberStyles(theme),
  deleteButton: {
    borderColor: '#ff4d4d',
    color: '#ff4d4d',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: 'rgba(255, 77, 77, 0.1)',
      borderColor: '#ff4d4d',
      boxShadow: '0 0 15px rgba(255, 77, 77, 0.4)',
    },
  },
}));

const UserPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const user = useSelector(state => state.session.user && state.session.user);

  const [item, setItem] = useState();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [messageSnacbar, setMessageSnacbar] = useState(null);
  const [requestDelete, setRequestDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    const fileSizeKB = selectedFile.size / 1024;
    const allowedExtensions = [".gpg", ".png", ".jpg", ".jpeg", ".webp"];
    const fileExtension = selectedFile.name.split(".").pop();
    if (!allowedExtensions.includes(`.${fileExtension.toLowerCase()}`)) {
      setMessageSnacbar("fileFormatMessage");
      return;
    } else {
      if (fileSizeKB < 200) setFile(selectedFile);
      else processImage(selectedFile);
    }
  };

  const processImage = (selectedFile) => {
    const reader = new FileReader();
    reader.readAsDataURL(selectedFile);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 800;
        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        const resizedFile = dataURLtoFile(dataUrl, selectedFile.name);
        if (resizedFile.size / 1024 < 200) setFile(resizedFile);
        else setMessageSnacbar("fileSizeMessage");
      };
    };
  };

  const dataURLtoFile = (dataurl, filename) => {
    let arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while (n--) { u8arr[n] = bstr.charCodeAt(n); }
    return new File([u8arr], filename, { type: mime });
  };

  const handleSubmit = async () => {
    setUploading(true);
    const response = await fetch('/api/users/addlogo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: file
    });
    if (response.ok) {
      dispatch(organizationActions.updateOrg(await response.json()));
      setUploading(false);
      setFile(null);
      setMessageSnacbar("logoUploadSuccessfully");
    } else setMessageSnacbar("logoNotUploadSuccessfully");
  };

  const handleCheckDevices = async () => {
    const response = await fetch('/api/devices?all=true');
    if (response.ok) {
      const res = await response.json();
      if (res?.length === 0) setRequestDelete('noDevices');
      else if (res?.length > 0) setRequestDelete('devicesExist');
    } else {
      const errorText = await response.text();
      let spliteText = errorText.split(' ')[0];
      setMessageSnacbar(spliteText ? spliteText : 'UnknownProblem');
    }
  };

  return (
      <EditItemView endpoint="users" item={item} setItem={setItem}>
        {item &&
            <>
              <Accordion defaultExpanded className={classes.accordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: '#00f2ff' }} />}>
                  <Typography className={classes.summaryText}>
                    {t('sharedRequired')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Box className={classes.fieldWrapper}>
                    <TextField
                        className={classes.neonInput}
                        fullWidth
                        value={item.name || ''}
                        onChange={event => setItem({ ...item, name: event.target.value })}
                        label={t('sharedName')}
                        variant="filled"
                        required />
                  </Box>
                  <Box className={classes.fieldWrapper}>
                    <TextField
                        className={classes.neonInput}
                        fullWidth
                        value={item.email || ''}
                        onChange={event => setItem({ ...item, email: event.target.value })}
                        label={t('userEmail')}
                        variant="filled"
                        required />
                  </Box>
                  <Box className={classes.fieldWrapper}>
                    <TextField
                        className={classes.neonInput}
                        fullWidth
                        value={item.phone || ''}
                        onChange={event => setItem({ ...item, phone: event.target.value })}
                        label={t('sharedPhone')}
                        variant="filled"
                        required />
                  </Box>
                  {user.administrator && !item.id && (
                      <Box className={classes.fieldWrapper}>
                        <TextField
                            className={classes.neonInput}
                            fullWidth
                            value={item.organization || ''}
                            onChange={event => setItem({ ...item, organization: event.target.value })}
                            label={t('userOrganization')}
                            variant="filled"
                            inputProps={{ maxLength: 12 }}
                            required/>
                      </Box>
                  )}
                  {user.subadmin && (
                      <Box className={classes.fieldWrapper}>
                        <TextField
                            className={classes.neonInput}
                            fullWidth
                            type={showPassword ? 'text' : 'password'}
                            onChange={event => {
                              const val = event.target.value;
                              setItem({ ...item, password: val, passwordThingwing: val })
                            }}
                            label={t('userPassword')}
                            variant="filled"
                            required
                            InputProps={{
                              endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" size="small">
                                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                  </InputAdornment>
                              ),
                            }}
                        />
                      </Box>
                  )}

                  {!user.administrator && user.subadmin && (
                      <Box marginTop={2}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={6}>
                            <Button
                                className={classes.uploadButton}
                                variant="contained"
                                startIcon={<CloudUploadIcon style={{ color: '#00f2ff' }} />}
                                component="label"
                                fullWidth
                            >
                              <span className={classes.textMobile}>
                                {t("selectLogo")}
                              </span>
                              <input type="file" accept="image/*" onChange={handleFileChange} hidden />
                            </Button>
                          </Grid>
                          <Grid item xs={6}>
                            <Button
                                className={classes.primaryGlowBtn}
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={!file || uploading}
                                fullWidth
                            >
                              {uploading ?
                                  <CircularProgress size={24} style={{ color: '#000' }} />
                                  :
                                  <span className={classes.textMobile}>{t("upload")}</span>
                              }
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              <Snackbar open={!!messageSnacbar} autoHideDuration={8000} onClose={() => setMessageSnacbar(null)}>
                <Alert onClose={() => setMessageSnacbar(null)} severity={messageSnacbar === "logoUploadSuccessfully" ? "success" : "error"}>
                  {t(messageSnacbar)}
                </Alert>
              </Snackbar>

              {user.subadmin && (
                  <Accordion className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: '#00f2ff' }} />}>
                      <Typography className={classes.summaryText}>
                        {t('sharedAttributes')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                      <EditAttributesView
                          attributes={item.attributes}
                          setAttributes={attributes => setItem({ ...item, attributes })}
                          definitions={userAttributes}
                      />
                    </AccordionDetails>
                  </Accordion>
              )}

              <Accordion className={classes.accordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: '#00f2ff' }} />}>
                  <Typography className={classes.summaryText}>
                    {t('sharedPermissions')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <FormControlLabel
                      className={classes.checkboxLabel}
                      control={<Checkbox
                          checked={item.readonly}
                          onChange={event => setItem({ ...item, readonly: event.target.checked }
                          )}
                      />
                  }
                      label={t('userReadonly')} />
                  <FormControlLabel
                      className={classes.checkboxLabel}
                      control={<Checkbox
                          checked={item.deviceReadonly}
                          onChange={event => setItem({ ...item, deviceReadonly: event.target.checked }
                          )}
                      />
                  }
                      label={t('userDeviceReadonly')} />
                </AccordionDetails>
              </Accordion>

              {item.id && item.id !== user.id && user.subadmin && (
                  <Accordion className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: '#00f2ff' }} />}>
                      <Typography className={classes.summaryText}>
                        {t('sharedConnections')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                      <Box className={classes.fieldWrapper}>
                        <LinkField
                            endpointAll="/api/devices?all=true"
                            endpointLinked={"/api/devices?userId=" + item.id}
                            baseId={item.id}
                            keyBase="userId"
                            keyLink="deviceId"
                            label={t('deviceTitle')}
                            variant="filled" />
                      </Box>
                      <Box className={classes.fieldWrapper}>
                        <LinkField
                            endpointAll="/api/groups?all=true"
                            endpointLinked={"/api/groups?userId=" + item.id}
                            baseId={item.id}
                            keyBase="userId"
                            keyLink="groupId"
                            label={t('settingsGroups')}
                            variant="filled" />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
              )}

              {item.id && user.subadmin && user.id === item.id && (
                  <Accordion className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: '#ff4d4d' }} />}>
                      <Typography className={classes.summaryText} style={{ color: '#ff4d4d' }}>
                        {t('DeleteAccount')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                      <Button
                          className={classes.deleteButton}
                          variant="outlined"
                          onClick={handleCheckDevices}
                          startIcon={<DeleteForeverOutlined />}
                          fullWidth
                      >
                        {t('DeleteAccount')?.toUpperCase()}
                      </Button>
                    </AccordionDetails>
                  </Accordion>
              )}
              <DeleteAccountDialog requestDelete={requestDelete} setRequestDelete={setRequestDelete} setMessageSnacbar={setMessageSnacbar} />
            </>
        }
      </EditItemView>
  );
}

export default UserPage;